import { Core } from "@faire/design-tokens";
import { TermsAndPrivacyPolicyDisclaimer } from "@faire/retailer-visitor-shared/components/Legal/TermsAndPrivacyPolicyDisclaimer";
import { useFaireDirectBrand } from "@faire/retailer-visitor-shared/hooks/FaireDirect/useFaireDirectBrand/useFaireDirectBrand";
import { useFaireDirectFirstOrderIncentive } from "@faire/retailer-visitor-shared/hooks/FaireDirect/useFaireDirectFirstOrderIncentive/useFaireDirectFirstOrderIncentive";
import { getSettingFaireDirectCreditExpiresInDays } from "@faire/retailer-visitor-shared/settings/getSettingFaireDirectCreditExpiresInDays";
import { Span } from "@faire/web--source/slate/Typography";
import { StrictLocalMsg } from "@faire/web--source/ui/lib/localization";
import React from "react";

interface IProps {
  textAlign?: "left" | "center";
  isFaireDirectSignUp: boolean;
  maxDiscountAmount: string | undefined;
  brandName: string | undefined;
}

export const Disclaimer: React.FC<
  Omit<IProps, "maxDiscountAmount" | "brandName">
> = ({ isFaireDirectSignUp, textAlign }) => {
  const { faireDirectBrand } = useFaireDirectBrand();
  const brandCompanyName = faireDirectBrand?.name;
  const { formattedMaxPercentageDiscountAmount } =
    useFaireDirectFirstOrderIncentive();

  return (
    <DisclaimerWithProps
      isFaireDirectSignUp={isFaireDirectSignUp}
      maxDiscountAmount={formattedMaxPercentageDiscountAmount}
      brandName={brandCompanyName ?? ""}
      textAlign={textAlign}
    />
  );
};

export const DisclaimerWithProps: React.FC<IProps> = ({
  isFaireDirectSignUp,
  maxDiscountAmount,
  brandName,
  textAlign,
}: IProps) => {
  const getFaireDirectDisclaimerLine = () => {
    if (!maxDiscountAmount || !brandName) {
      return null;
    }

    return (
      <StrictLocalMsg
        defaultMessage="*Available for retailers who are new to Faire. Max discount of {maximumDiscountMoney} & 1 year of free shipping with {brandCompanyName} only. Expires {expiresInDaysNumber} days after signup."
        description={{
          text: "Disclaimer for the Faire Direct signup credit",
          img: "https://cdn.faire.com/fastly/23a39450c40259b63373709fd9e039e422665966d9b78a0602b36bedc0312b42.png",
        }}
        values={{
          maximumDiscountMoney: maxDiscountAmount,
          brandCompanyName: brandName,
          expiresInDaysNumber: getSettingFaireDirectCreditExpiresInDays(),
        }}
      />
    );
  };

  return (
    <Span
      variant="labelSansRegular"
      color={Core.text.subdued}
      align={isFaireDirectSignUp ? "start" : (textAlign ?? "center")}
    >
      {/* Extra syntax needed for the space after the fdDisclaimerLine */}
      {isFaireDirectSignUp ? <>{getFaireDirectDisclaimerLine()} </> : null}
      <TermsAndPrivacyPolicyDisclaimer />
    </Span>
  );
};
